import type { ResolvedBetterstartConfig } from '@cli-types/core-engine/config.js'
import type { GeneratedFile, GeneratorOptions, Schema } from '@cli-types/core-engine/schema.js'
import type { PipelineResult } from '@cli-types/next-adapter/generators.js'
import { normalizeSchemaSlotLayout } from '@core-engine/schema/schema-reader/normalize-schema-slot-layout.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace/resolve-admin-namespace.js'
import { resolveExtensionNamespace } from '@next-adapter/config/paths/resolve-extension-namespace.js'
import { resolveProjectPaths } from '@next-adapter/config/paths/resolve-project-paths.js'
import { generateSingleActions } from '@next-adapter/generators/actions/single-actions/generate-single-actions.js'
import { generateDatabase } from '@next-adapter/generators/database/generate-database.js'
import { generateSingleForm } from '@next-adapter/generators/form/form-single/generate-single-form.js'
import { generateSingleHook } from '@next-adapter/generators/hook/generate-single-hook.js'
import { namespaceGeneratedFiles } from '@next-adapter/generators/namespace-output/namespace-generated-files.js'
import { updateNavigation } from '@next-adapter/generators/navigation.js'
import { generateSinglePage } from '@next-adapter/generators/single-page.js'
import { updateWebhookEvents } from '@next-adapter/generators/webhook-events/update-webhook-events.js'
import { loadRelationshipSchemas } from './load-relationship-schemas.js'

export function runSinglePipeline(
  schema: Schema,
  cwd: string,
  config: ResolvedBetterstartConfig,
  options: GeneratorOptions = {}
): PipelineResult {
  const normalizedSchema = normalizeSchemaSlotLayout(schema)
  const paths = resolveProjectPaths(config)
  const outputNamespace = resolveExtensionNamespace(normalizedSchema.name, options.outputNamespace)
  const adminNamespace = resolveAdminNamespace(config.frameworkConfig.next.namespace)
  const namespacedOptions = {
    ...options,
    outputNamespace,
    adminDir: paths.adminDir,
    adminRoutePath: adminNamespace.routePath,
    adminImportAlias: adminNamespace.alias
  }
  const files: GeneratedFile[] = []
  const errors: string[] = []
  const relatedSchemas = loadRelationshipSchemas(normalizedSchema, cwd, paths.schemasDir)

  const steps: { name: string; run: () => GeneratedFile[] }[] = [
    {
      name: 'Database schema',
      run: () =>
        generateDatabase(normalizedSchema, paths.adminDbDir, namespacedOptions, relatedSchemas)
          .files
    },
    {
      name: 'Server actions',
      run: () =>
        generateSingleActions(normalizedSchema, paths.adminActionsDir, {
          force: options.force,
          nextMajorVersion: config.nextMajorVersion,
          outputNamespace
        }).files
    },
    {
      name: 'React Query hook',
      run: () => generateSingleHook(normalizedSchema, paths.adminHooksDir, namespacedOptions).files
    },
    {
      name: 'Form',
      run: () => generateSingleForm(normalizedSchema, paths.pagesDir, namespacedOptions).files
    },
    {
      name: 'Single page',
      run: () => generateSinglePage(normalizedSchema, paths.pagesDir, namespacedOptions).files
    },
    {
      name: 'Navigation',
      run: () =>
        updateNavigation(normalizedSchema, paths.adminNavigationDir, namespacedOptions).files
    },
    {
      name: 'Webhook events',
      run: () => updateWebhookEvents(normalizedSchema, paths.adminWebhookEventsDir, 'single').files
    }
  ]

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    const stepNum = i + 1
    try {
      const result = step.run()
      files.push(...result)
      if (!options.silent) console.log(`   ${stepNum}. ${step.name} ✓`)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      errors.push(`${step.name}: ${msg}`)
      if (!options.silent) console.error(`   ${stepNum}. ${step.name} ✗ — ${msg}`)
    }
  }

  return {
    success: errors.length === 0,
    files: namespaceGeneratedFiles(files, config.frameworkConfig.next.namespace),
    errors
  }
}
